import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Compass, Shield, Settings, TrendingUp, CheckCircle } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const Methodology = () => {
  const focuses = [
    {
      icon: Shield,
      title: 'Reducing mechanical and tooling risk',
      description: 'Prioritizing machine safety and tool protection in all recommendations'
    },
    {
      icon: Settings,
      title: 'Working within real machine limitations',
      description: 'Understanding actual capabilities, not ideal specifications'
    },
    {
      icon: Compass,
      title: 'Supporting hobby and mid-weight CNC systems',
      description: 'Tailored guidance for real-world workshop machines'
    },
    {
      icon: TrendingUp,
      title: 'Encouraging incremental improvement',
      description: 'Progress through testing, adjustment, and refinement rather than one-shot perfection'
    }
  ];

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.METHODOLOGY)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
    <div className="max-w-4xl mx-auto space-y-8 p-4" data-testid="methodology-page">
      {/* Page Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 border border-[#e67e22]/50 rounded-sm bg-[#e67e22]/10">
          <Compass className="w-5 h-5 text-[#e67e22]" />
          <span className="text-sm font-bold text-[#e67e22] uppercase tracking-wide">Our Approach</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Our Methodology
        </h1>
      </div>

      {/* Main Content Card */}
      <Card className="bg-[#242424] border-[#3a3a3a]">
        <CardContent className="pt-6 space-y-6">
          {/* Introduction */}
          <div className="prose prose-invert max-w-none">
            <p className="text-base sm:text-lg text-[#d6d6d6] leading-relaxed">
              CNC-Helpdesk is designed around how CNC machines are <span className="text-[#e67e22] font-semibold">actually built and used</span> — not how they appear in ideal specifications.
            </p>
            
            <p className="text-[#a3a3a3] leading-relaxed mt-4">
              The system asks structured, practical questions about machine configuration, materials, and goals. Where information is missing or unclear, CNC-Helpdesk applies conservative, experience-based defaults that prioritize machine safety and tool protection.
            </p>
          </div>

          {/* Recommendations Focus */}
          <div className="mt-8">
            <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Recommendations Focus On:
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {focuses.map((focus, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm hover:border-[#e67e22]/30 transition-colors"
                >
                  <div className="w-10 h-10 bg-[#e67e22]/10 border border-[#e67e22]/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <focus.icon className="w-5 h-5 text-[#e67e22]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{focus.title}</h3>
                    <p className="text-[#a3a3a3] text-xs">{focus.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experience Statement */}
          <div className="mt-8 p-5 bg-[#1f1f1f] border-l-4 border-[#e67e22] rounded-r-sm">
            <p className="text-[#d6d6d6] leading-relaxed">
              This approach reflects <span className="text-white font-semibold">hands-on CNC work</span>, iterative mechanical builds, electronics projects, and real-world troubleshooting — where progress is achieved through testing, adjustment, and refinement.
            </p>
          </div>

          {/* Closing Statement */}
          <div className="mt-6 flex items-start gap-3 p-4 bg-[#e67e22]/5 border border-[#e67e22]/20 rounded-sm">
            <CheckCircle className="w-6 h-6 text-[#e67e22] flex-shrink-0 mt-0.5" />
            <p className="text-[#d6d6d6]">
              <span className="text-white font-semibold">CNC-Helpdesk provides a reliable starting point</span> that users can adapt as their machines, skills, and understanding evolve.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default Methodology;
